import _ from 'lodash';
import { compareTwoDates } from 'utils/moment';
import { lineItemsService } from 'services';
import { convertToFloat } from '../utils/math';
import { window } from 'browser-monads';

function normalizePrice(val) {
  const num = Number.parseFloat(String(val ?? '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(num) ? Number(num.toFixed(2)) : 0;
}

function isHttpUrl(s) {
  return typeof s === 'string' && /^https?:\/\//i.test(s);
}
// Allow http(s), data:image/*, and blob: for cart thumbnails
function isAllowedImgSrc(s) {
  return (
    typeof s === 'string' &&
    (/^https?:\/\//i.test(s) || /^data:image\//i.test(s) || /^blob:/i.test(s))
  );
}

/* ---------- bytes helper (SSR-safe) ---------- */
function jsonBytes(obj) {
  try {
    const s = JSON.stringify(obj);
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s).length;
    // Node fallback
    // eslint-disable-next-line no-undef
    return Buffer.from(s, 'utf8').length;
  } catch {
    return 0;
  }
}

/* ---------- compact metadata ---------- */
function compactMetadata(meta) {
  if (!meta) return {};
  const copy = { ...meta };

  // Strip base64 thumbnail
  if (typeof copy.thumbnail === 'string' && copy.thumbnail.startsWith('data:')) {
    delete copy.thumbnail;
  }

  // Keep under ~7KB (Snipcart soft ~8KB)
  if (jsonBytes(copy) > 7000) {
    delete copy.offer;
    delete copy.user;      // Snipcart collects forms anyway
    delete copy.delivery;  // UI can still compute totals
  }
  return copy;
}

/* ---------- Snipcart guards ---------- */
function ensureSnipcart(where = '') {
  const sn = window?.Snipcart;
  if (!sn?.api) throw new Error(`Snipcart not ready${where ? ` (${where})` : ''}`);
  return sn;
}

/* ---------- State helpers ---------- */
function getCartStateItems() {
  try {
    return window?.Snipcart?.store?.getState?.().cart?.items?.items ?? [];
  } catch {
    return [];
  }
}
function getCartItems() {
  return getCartStateItems();
}
function getCartItem(uniqueId) {
  return getCartStateItems().find(i => i.uniqueId === uniqueId);
}

/* ---------- Events ---------- */
function onCartReady() {
  const sn = window?.Snipcart;
  if (!sn?.events?.on) return;
  sn.events.on('item.removed', (cartItem) => {
    cartItemsService.handleRemoveCartItem(cartItem);
  });
}

/* ---------- Add ---------- */
async function addItemToCart(itemData) {
  ensureSnipcart('add');

  const imgs = Array.isArray(itemData.images)
    ? itemData.images.filter(isAllowedImgSrc)
    : [];

  const payload = {
    id: String(itemData.itemId),
    name: String(itemData.itemName ?? 'Item'),
    price: normalizePrice(itemData.itemPrice ?? itemData.price),
    quantity: Number(itemData.quantity) > 0 ? Number(itemData.quantity) : 1,
    ...(isHttpUrl(itemData.itemUrl) ? { url: String(itemData.itemUrl) } : {}),
    ...(isAllowedImgSrc(itemData.image) ? { image: itemData.image } : {}),
    ...(imgs.length ? { images: imgs } : {}),
    metadata: compactMetadata(itemData.metadata),
  };

  return window.Snipcart.api.cart.items.add(payload);
}

/* ---------- Remove helper ---------- */
async function removeItemByUniqueId(uniqueId) {
  const sn = ensureSnipcart('remove');
  if (sn.api?.cart?.items?.remove) {
    return sn.api.cart.items.remove(uniqueId);
  }
  // legacy path
  if (sn.api?.items?.remove) {
    return sn.api.items.remove(uniqueId);
  }
}

/* ---------- UPDATE → REPLACEMENT ---------- */
async function updateItemToCart(cartItemId, itemData) {
  ensureSnipcart('update');

  const items = getCartStateItems();
  const byUnique = items.find(i => i.uniqueId === cartItemId);
  const byLineId = itemData?.metadata?.lineItemId
    ? items.find(i => i.metadata?.lineItemId === itemData.metadata.lineItemId)
    : undefined;
  const byProdId = items.find(i => i.id === String(itemData.itemId));
  const target = byUnique || byLineId || byProdId;

  const imgs = Array.isArray(itemData.images)
    ? itemData.images.filter(isAllowedImgSrc)
    : [];

  const addPayload = {
    id: String(itemData.itemId),
    name: String(itemData.itemName ?? 'Item'),
    price: normalizePrice(itemData.itemPrice ?? itemData.price),
    quantity: Number(itemData.quantity) > 0 ? Number(itemData.quantity) : 1,
    // Keep a URL on the line; prefer the one passed, else reuse the existing one
    ...(isHttpUrl(itemData.itemUrl)
      ? { url: String(itemData.itemUrl) }
      : (isHttpUrl(target?.url) ? { url: target.url } : {})),
    ...(isAllowedImgSrc(itemData.image) ? { image: itemData.image } : {}),
    ...(imgs.length ? { images: imgs } : {}),
    metadata: compactMetadata(itemData.metadata),
  };

  if (target) {
    try { await removeItemByUniqueId(target.uniqueId); } catch {}
  }
  return window.Snipcart.api.cart.items.add(addPayload);
}

/* ---------- Update a whole cartItem (legacy callers) ---------- */
async function updateCartItemField(cartItem) {
  ensureSnipcart('update field');

  const items = getCartStateItems();
  const target = items.find(i => i.uniqueId === cartItem.uniqueId) ||
                 items.find(i => i.metadata?.lineItemId === cartItem?.metadata?.lineItemId);

  const imgs = Array.isArray(cartItem.images)
    ? cartItem.images.filter(isAllowedImgSrc)
    : [];

  const addPayload = {
    id: String(target?.id ?? cartItem.id),
    name: String(cartItem.name ?? 'Item'),
    price: normalizePrice(cartItem.price),
    quantity: Number(cartItem.quantity) > 0 ? Number(cartItem.quantity) : 1,
    ...(isHttpUrl(cartItem.url) ? { url: cartItem.url } : {}),
    ...(isAllowedImgSrc(cartItem.image) ? { image: cartItem.image } : {}),
    ...(imgs.length ? { images: imgs } : {}),
    metadata: compactMetadata(cartItem.metadata),
  };

  if (target) {
    try { await removeItemByUniqueId(target.uniqueId); } catch {}
  }
  return window.Snipcart.api.cart.items.add(addPayload);
}

/* ---------- Misc ---------- */
function clearCart() {
  const sn = ensureSnipcart('clear');
  if (sn.api?.cart?.clear) return sn.api.cart.clear();
  if (sn.api?.items?.clear) return sn.api.items.clear();
  return Promise.resolve();
}
function openCart() {
  try { window?.Snipcart?.api?.theme?.cart?.open?.(); } catch (e) {}
}

function getSameDeliveryItems(addr, dTime, courior) {
  const cartItems = getCartItems();
  return cartItems.filter(cartItem =>
    compareTwoDates(cartItem.metadata.delivery.dispatchDate, dTime) &&
    compareDeliveryAddresses(addr, cartItem.metadata.user) &&
    cartItem.metadata.delivery.option === courior
  );
}
function compareWithCartItem(addr, dTime, courior, cartItem) {
  return compareTwoDates(cartItem.metadata.delivery.dispatchDate, dTime) &&
    compareDeliveryAddresses(addr, cartItem.metadata.user) &&
    cartItem.metadata.delivery.option === courior;
}
function compareDeliveryAddresses(addr1, addr2) {
  return addr1.address1.toUpperCase() === addr2.address1.toUpperCase() &&
    (addr1.address2 || '').toUpperCase() === (addr2.address2 || '').toUpperCase() &&
    addr1.country.toUpperCase() === addr2.country.toUpperCase() &&
    addr1.locality.toUpperCase() === addr2.locality.toUpperCase() &&
    String(addr1.postcode) === String(addr2.postcode) &&
    (addr1.state || '').toUpperCase() === (addr2.state || '').toUpperCase();
}

async function handleRemoveCartItem(cartItem) {
  try {
    // remove lineItem from db
    lineItemsService.removeLineItem(cartItem.metadata.lineItemId);

    if (cartItem.metadata.delivery.price !== "$0.00") {
      const matchedItems = getSameDeliveryItems(
        cartItem.metadata.user,
        cartItem.metadata.delivery.dispatchDate,
        cartItem.metadata.delivery.option
      );
      if (matchedItems.length > 0) {
        await lineItemsService.updateLineItemDeliveryPrice(
          matchedItems[0].metadata.lineItemId,
          cartItem.metadata.delivery.price
        );
        const newItemData = { ...matchedItems[0] };
        newItemData.metadata.delivery.price = cartItem.metadata.delivery.price;
        const recalculatedTotal = convertToFloat(
          convertToFloat(newItemData.price) + convertToFloat(newItemData.metadata.delivery.price)
        );
        newItemData.metadata.price.totalNumber = recalculatedTotal;
        newItemData.metadata.price.total = `$ ${recalculatedTotal.toFixed(2)}`;
        newItemData.price = recalculatedTotal;
        await updateCartItemField(newItemData);
      }
    }
  } catch {
    // non-fatal
  }
}

async function handleUpdateCartItem(cartItem) {
  try {
    const matchedItems = getSameDeliveryItems(
      cartItem.metadata.user,
      cartItem.metadata.delivery.dispatchDate,
      cartItem.metadata.delivery.option
    );
    if (matchedItems.length > 0) {
      await lineItemsService.updateLineItemDeliveryPrice(
        matchedItems[0].metadata.lineItemId,
        cartItem.metadata.delivery.price
      );
      const newItemData = { ...matchedItems[0] };
      newItemData.metadata.delivery.price = cartItem.metadata.delivery.price;
      const recalculatedTotal = convertToFloat(
        convertToFloat(newItemData.price) + convertToFloat(newItemData.metadata.delivery.price)
      );
      newItemData.metadata.price.totalNumber = recalculatedTotal;
      newItemData.metadata.price.total = `$ ${recalculatedTotal.toFixed(2)}`;
      newItemData.price = recalculatedTotal;
      await updateCartItemField(newItemData);
    }
  } catch {
    // non-fatal
  }
}

export const cartItemsService = {
  getCartItems,
  getCartItem,
  addItemToCart,
  updateItemToCart,          // now remove→add internally (quiet)
  clearCart,
  openCart,
  getSameDeliveryItems,
  compareDeliveryAddresses,
  handleRemoveCartItem,
  handleUpdateCartItem,
  onCartReady,
  compareWithCartItem,
};
